<script setup>
import SlideOver from '@/components/SlideOver.vue';

import { useModal } from '@/composables/useModal.js';
import AppButton from '@/components/AppButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import { ref } from 'vue';
import { useAxios } from '@vueuse/integrations/useAxios';
import { watchThrottled } from '@vueuse/core';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';

const props = defineProps({
    endpointUrl: {
        required: true,
        type: String,
    },
});

const { isOpen, dismiss } = useModal(false);

const search = ref('');

const { execute, data: students } = useAxios(props.endpointUrl, { method: 'GET' }, { immediate: false });

watchThrottled(
    search,
    (value) => {
        if (value.length) {
            execute({ params: { search: search.value } });
        } else {
            students.value = [];
        }
    },
    { throttle: 750 },
);
</script>

<template>
    <AppButton element="button" @click="isOpen = true">
        {{ $tc('messages.duels', 1) }}
    </AppButton>
    <SlideOver :show="isOpen" @close="dismiss">
        <div class="mt-1">
            <FormLabel :value="$t('messages.search')" for="search" />
            <FormInput
                required
                id="search"
                v-model.trim="search"
                :placeholder="$t('messages.name')"
                name="search"
                type="text"
                max="60"
                class="mt-1 w-full"
            />
        </div>

        <div>
            <div>
                {{ $t('messages.who_do_you_want_to_duel') }}
            </div>

            <div>
                <AlertMessage class="mt-5 inline-block" type="info">
                    <template v-slot:title>
                        {{ $t('messages.information') }}
                    </template>
                    <template v-slot:body>
                        {{ $t('messages.one_duel_per_person_at_at_time') }}
                    </template>
                </AlertMessage>
            </div>

            <ul class="odd:bg-white even:bg-gray-400 mt-3" v-auto-animate>
                <li
                    v-for="(student, index) in students"
                    :key="student.id"
                    class="my-1 p-1"
                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                >
                    <div class="flex justify-between items-center">
                        <div class="flex space-x-2 items-center">
                            <img
                                :src="student.avatar_or_default"
                                :alt="student.first_name_and_initial"
                                :title="student.first_name_and_initial"
                                class="rounded-full h-10"
                            />
                            <div>
                                <div>
                                    {{ student.first_name_and_initial }}
                                </div>
                                <div>
                                    {{ student.group_name }}
                                </div>
                            </div>
                        </div>
                        <div>
                            <InertiaSquareLinkButton :href="student.url" action="quiz" v-if="student.url" />
                            <div v-else class="text-xs text-yellow-600">
                                ({{ $t('messages.only_one_duel_at_a_time') }})
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </SlideOver>
</template>
